// Keyboard-first lists (design spec §2.1 `j` / `k`, `Enter`; spec 06 §Inbox "j/k/e/⏎"; Work OS
// use-list-keys.ts): inside a list, `j` / ArrowDown and `k` / ArrowUp move focus between its
// `[data-list-row]` rows (clamped at the ends), `Enter` on a row opens it, and the caller's
// letters (`e` done in the inbox) act on the focused row. Ignored with Cmd, Ctrl or Alt held,
// while typing in a field, and while a menu or listbox is open.
import { type RefObject, useEffect, useRef } from "react";

export type ListKeyActions = {
  onOpen?: (row: HTMLElement) => void;
  /** Letter → action on the focused row. */
  letters?: Record<string, (row: HTMLElement) => void>;
};

const typing = (el: Element | null) =>
  el instanceof HTMLElement &&
  (el.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) ||
    el.getAttribute("role") === "textbox");

export function listKeyFor(e: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey">) {
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  if (e.key === "j" || e.key === "ArrowDown") return "next" as const;
  if (e.key === "k" || e.key === "ArrowUp") return "prev" as const;
  if (e.key === "Enter") return "open" as const;
  return null;
}

/** The row element a focused node sits in. */
const rowOf = (el: Element | null) =>
  (el?.closest("[data-list-row]") as HTMLElement | null) ?? null;

/** The row's own focus target: its title button or link, else its first control, else the row. */
export function focusRow(row: HTMLElement | null | undefined) {
  if (!row) return;
  const target =
    row.querySelector<HTMLElement>("[data-row-focus]") ??
    row.querySelector<HTMLElement>("a[href], button") ??
    row;
  target.focus();
  target.scrollIntoView?.({ block: "nearest" });
}

/** The task a row stands for: list rows and cards carry `data-task-id`, table rows `data-row-id`. */
export function taskIdOf(row: HTMLElement): string | null {
  return (
    row.dataset.taskId ??
    row.dataset.rowId ??
    row.querySelector<HTMLElement>("[data-task-id]")?.dataset.taskId ??
    null
  );
}

/** Whether a dialog, menu or listbox owns the keyboard. */
const overlayOpen = () =>
  Boolean(document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]'));

/**
 * `c` creates (Linear's key; the Docs screen passes `n`): bound on the window while the screen is mounted. Ignored while
 * typing, with any modifier, on key repeat, and while a dialog, menu or listbox is open.
 */
export function useCreateKey(onCreate: () => void, enabled = true, key = "c") {
  const latest = useRef(onCreate);
  latest.current = onCreate;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== key || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey || e.repeat) return;
      if (typing(document.activeElement) || overlayOpen()) return;
      e.preventDefault();
      latest.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, key]);
}

/** go-shortcuts' chord window (`g` then a letter). */
const CHORD_WINDOW_MS = 1500;

export function useListKeys(
  root: RefObject<HTMLElement | null>,
  actions: ListKeyActions,
  enabled = true,
  /**
   * INBOX-PAGE-MAIN: listen on `window` (a full page's one list): j/k work before focus is in
   * the list. Focus inside another region (a dialog, the peek, another list) keeps its own keys.
   */
  onWindow = false,
) {
  useEffect(() => {
    const el = root.current;
    if (!el || !enabled) return;
    // On the window, a `g` chord's second key (`g k` → KPIs) is the chord's, not the list's.
    let chordUntil = 0;
    const onKey = (e: KeyboardEvent) => {
      if (onWindow) {
        if (e.defaultPrevented) return;
        const now = Date.now();
        if (chordUntil > now) {
          chordUntil = 0;
          return;
        }
        if (e.key === "g" && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey)
          chordUntil = now + CHORD_WINDOW_MS;
      }
      if (e.repeat && e.key === "Enter") return;
      if (typing(document.activeElement) && !rowOf(document.activeElement)) return;
      if (document.querySelector('[role="menu"], [role="listbox"]')) return;
      if (onWindow) {
        // From the page's chrome (the rail link just clicked, the bell after its peek closed)
        // j/k start in the list; never while an overlay is open or another list has the focus.
        const active = document.activeElement;
        const inside = !!active && el.contains(active);
        if (!inside && (overlayOpen() || rowOf(active) || typing(active))) return;
      }
      const rows = Array.from(el.querySelectorAll<HTMLElement>("[data-list-row]"));
      if (!rows.length) return;
      const current = el.contains(document.activeElement) ? rowOf(document.activeElement) : null;
      const i = current ? rows.indexOf(current) : -1;
      const kind = listKeyFor(e);
      if (kind === "next" || kind === "prev") {
        e.preventDefault();
        const next =
          kind === "next" ? Math.min(rows.length - 1, i + 1) : Math.max(0, i < 0 ? 0 : i - 1);
        focusRow(rows[next]);
        return;
      }
      if (!current) return;
      if (kind === "open") {
        // Enter on a button or link inside the row (an action) keeps its own meaning.
        const t = e.target as HTMLElement;
        if (t !== current && !t.hasAttribute("data-row-focus")) return;
        e.preventDefault();
        actions.onOpen?.(current);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const act = actions.letters?.[e.key];
      if (act) {
        e.preventDefault();
        act(current);
      }
    };
    const target: HTMLElement | Window = onWindow ? window : el;
    target.addEventListener("keydown", onKey as EventListener);
    return () => target.removeEventListener("keydown", onKey as EventListener);
  }, [root, actions, enabled, onWindow]);
}
